import { type ChatContext } from '@myre/app-agent-generated/types/ChatContext';
import { stripUrlQueryAndFragment } from '@myre/common-primitives';

import { computed, inject, Injectable, signal } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { isEqual, omit } from 'lodash-es';
import {
  combineLatest,
  distinctUntilChanged,
  filter,
  map,
  shareReplay,
  startWith,
} from 'rxjs';

import {
  routeParamsPath,
  RouterStateSerializer,
} from '../../services/auth/dispatch.guard';

/**
 * Service for managing chat context.
 * Provides a centralized way to access and control the current context
 * used for chat configuration and message requests.
 *
 * Features:
 * - Deduplicates context emissions to prevent unnecessary API calls
 * - Automatically updates context on route navigation (when enabled)
 * - Provides both synchronous (signals) and async (observables) access patterns
 */
@Injectable({ providedIn: 'root' })
export class ChatContextService {
  private readonly _router = inject(Router);
  private readonly _routerStateSerializer = inject(RouterStateSerializer);

  /** Whether context is enabled (user can disable it) */
  private readonly _enabled = signal(true);

  /** Whether context is enabled */
  public readonly enabled = this._enabled.asReadonly();

  /** Observable of enabled state for combining with navigation events */
  private readonly _enabled$ = toObservable(this._enabled);

  /**
   * Ids the user removed from the zone, stamped with the page they were removed
   * on: the removal expresses "not for this page", so another page reads its own
   * ids instead of being silently muted too.
   */
  private readonly _excluded = signal<{
    page: string;
    keys: readonly string[];
  }>({ page: '', keys: [] });

  /** Observable of the removed ids, for combining with navigation events */
  private readonly _excluded$ = toObservable(this._excluded);

  /** Ids removed from the current page's context. */
  public readonly excludedKeys = computed(() =>
    this._excludedKeysFor(this.pageKey()),
  );

  /**
   * Observable that emits on navigation end events.
   * Used internally to trigger context updates on route changes.
   */
  private readonly _navigationEnd$ = this._router.events.pipe(
    filter((event): event is NavigationEnd => event instanceof NavigationEnd),
    startWith(null), // Trigger initial emission
  );

  /**
   * Observable that emits the context for API calls, deduplicated.
   * Only emits when the effective API context has actually changed.
   * Combines navigation events, enabled state changes and id removals.
   *
   * When enabled: returns full context with all params, minus the removed ids.
   * When disabled: returns context with only customerId (always required).
   */
  public readonly contextForApi$ = combineLatest([
    this._navigationEnd$,
    this._enabled$,
    this._excluded$,
  ]).pipe(
    map(() => this._getContextForApiInternal()),
    distinctUntilChanged((prev, curr) => isEqual(prev, curr)),
    shareReplay(1),
  );

  /**
   * Signal that provides reactive access to the API context.
   * Automatically updates on navigation.
   */
  public readonly contextForApi = toSignal(this.contextForApi$, {
    initialValue: this._getContextForApiInternal(),
  });

  /**
   * Internal signal that tracks the current context value.
   * Updated reactively when navigation completes.
   * Always contains the full context from the router state.
   */
  private readonly _context = signal<ChatContext<string>>(
    this._getContextInternal(),
  );

  /**
   * Signal that provides reactive access to the current context.
   * Automatically updates on navigation.
   * Always contains the full context — use `enabled()` to check if context is active.
   */
  public readonly context = this._context.asReadonly();

  private readonly _contextKeyPath = signal<readonly string[]>(
    this._getContextKeyPathInternal(),
  );

  /**
   * The ids of the current route, root first: the last one is the entity the
   * page is about, its predecessors are the scopes it hangs under.
   */
  public readonly contextKeyPath = this._contextKeyPath.asReadonly();

  /**
   * What a removal or a fold is remembered against. Query params are left out:
   * filtering a list or opening a dataroom node navigates without leaving the
   * page, and must not undo what the user did to the zone.
   */
  public readonly pageKey = computed(() =>
    stripUrlQueryAndFragment(this._context().url),
  );

  constructor() {
    // Subscribe to navigation events to trigger context updates.
    // This is necessary because router state changes don't trigger Angular signals.
    this._navigationEnd$.subscribe(() => {
      const newContext = this._getContextInternal();
      const currentContext = this._context();

      if (!isEqual(currentContext, newContext)) {
        this._context.set(newContext);
      }

      const newKeyPath = this._getContextKeyPathInternal();

      if (!isEqual(this._contextKeyPath(), newKeyPath)) {
        this._contextKeyPath.set(newKeyPath);
      }
    });
  }

  /**
   * Removes one id from the context, for as long as the route carrying it.
   */
  public excludeKey(key: string): void {
    const page = stripUrlQueryAndFragment(this._getContextInternal().url);
    const keys = this._excludedKeysFor(page);

    if (keys.includes(key)) {
      return;
    }

    this._excluded.set({ page, keys: [...keys, key] });
  }

  /**
   * Disables the context.
   * When disabled, the context zone is hidden and API calls use minimal context.
   */
  public disable(): void {
    this._enabled.set(false);
  }

  /**
   * Toggles the context enabled state.
   */
  public toggle(): void {
    this._enabled.update(v => !v);
  }

  /**
   * Internal method to get the full context from router state.
   * Always returns the full context regardless of enabled state.
   */
  private _getContextInternal(): ChatContext<string> {
    return this._routerStateSerializer.serialize(
      this._router.routerState.snapshot,
    ) as ChatContext<string>;
  }

  private _getContextKeyPathInternal(): readonly string[] {
    return routeParamsPath(this._router.routerState.snapshot);
  }

  /**
   * Internal method to get context for API calls.
   * When enabled: returns the full context, minus the ids removed on this page.
   * When disabled: returns context with only customerId (always required).
   */
  private _getContextForApiInternal(): ChatContext<string> {
    const fullContext = this._getContextInternal();

    if (!this._enabled()) {
      return {
        ...fullContext,
        params: { customerId: fullContext.params.customerId },
      };
    }

    const excludedKeys = this._excludedKeysFor(
      stripUrlQueryAndFragment(fullContext.url),
    );

    if (!excludedKeys.length) {
      return fullContext;
    }

    return {
      ...fullContext,
      params: {
        ...omit(fullContext.params, excludedKeys),
        // The customer scopes every call: a removal never reaches it.
        customerId: fullContext.params.customerId,
      },
    };
  }

  private _excludedKeysFor(page: string): readonly string[] {
    const excluded = this._excluded();

    return excluded.page === page ? excluded.keys : [];
  }
}
